import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, MapPin, Users, Ticket, Plus, Clock, Search, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatNaira } from "@/data/venues";
import { eventsApi, type EventResponse } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/api";

export const Route = createFileRoute("/events/")({
  head: () => ({
    meta: [
      { title: "Events — OyoBooking" },
      {
        name: "description",
        content:
          "Browse and register for upcoming events in Oyo State. Concerts, conferences, parties and more.",
      },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/events" }],
  }),
  component: EventsPage,
});

type TierType = "regular" | "standard" | "early_bird" | "vip" | "vvip" | "table";

interface EventTier {
  id: string;
  name: string;
  tierType: TierType;
  price: number;
  capacity: number;
  sold: number;
  status: string;
}

interface Event {
  id: string;
  eventRef: string;
  name: string;
  organizer: string;
  description?: string;
  date: string;
  time?: string;
  venueName: string;
  image?: string;
  status: string;
  totalRegistrations: number;
  tiers?: EventTier[];
}

const TIER_LABELS: Record<string, string> = {
  regular: "Regular",
  standard: "Standard",
  early_bird: "Early Bird",
  vip: "VIP",
  vvip: "VVIP",
  table: "Table",
};

const TIER_COLORS: Record<string, string> = {
  regular: "bg-blue-100 text-blue-700",
  standard: "bg-slate-100 text-slate-700",
  early_bird: "bg-rose-100 text-rose-700",
  vip: "bg-amber-100 text-amber-700",
  vvip: "bg-purple-100 text-purple-700",
  table: "bg-green-100 text-green-700",
};


function EventCard({ event }: { event: Event }) {
  const lowestTier = event.tiers?.reduce((min, t) => t.price < min.price ? t : min, event.tiers[0]);
  const dateObj = new Date(event.date);
  const month = dateObj.toLocaleString("en-NG", { month: "short" }).toUpperCase();
  const day = dateObj.getDate();
  const weekday = dateObj.toLocaleString("en-NG", { weekday: "short" });
  const allSoldOut = event.tiers && event.tiers.length > 0 && event.tiers.every(t => t.status.toLowerCase() === "sold_out");

  return (
    <div className="group/card relative flex flex-col">
      <Link
        to="/events/$eventId"
        params={{ eventId: event.id }}
        className="relative block aspect-[4/3] overflow-hidden rounded-xl bg-accent/30"
      >
        {event.image ? (
          <img src={resolveMediaUrl(event.image)} alt={event.name} loading="lazy"
            className="size-full object-cover transition-transform duration-500 group-hover/card:scale-105" />
        ) : (
          <div className="size-full bg-gradient-to-br from-primary/15 via-primary/5 to-accent/30 flex items-center justify-center">
            <Ticket className="size-14 text-primary/20" />
          </div>
        )}
        <span className="absolute left-3 top-3 rounded-lg bg-white/95 px-2.5 py-1 text-center shadow-sm backdrop-blur-sm min-w-[46px]">
          <p className="text-[9px] font-bold text-primary leading-tight">{month}</p>
          <p className="text-base font-bold leading-none text-foreground">{day}</p>
          <p className="text-[9px] text-muted-foreground leading-tight">{weekday}</p>
        </span>
        {allSoldOut ? (
          <span className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold shadow-sm bg-red-500 text-white">
            SOLD OUT
          </span>
        ) : (
          <span className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold shadow-sm ${
            event.status === "active" ? "bg-green-500 text-white" : "bg-muted text-foreground"
          }`}>
            {event.status}
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
      </Link>

      <Link to="/events/$eventId" params={{ eventId: event.id }} className="mt-3 block space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-[15px] leading-snug text-foreground line-clamp-1">{event.name}</h3>
          {event.tiers && event.tiers.length > 0 && (
            <span className="flex shrink-0 items-center gap-0.5 text-sm font-medium text-muted-foreground">
              <Ticket className="size-3.5" />{event.tiers.length}
            </span>
          )}
        </div>
        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="size-3.5 shrink-0" /> {event.venueName}
        </p>
        <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="size-3.5 shrink-0" />{event.time || "TBD"} · {event.organizer}
          </span>
        </div>
        {event.tiers && event.tiers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {event.tiers.slice(0, 3).map(t => (
              <span key={t.id} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${TIER_COLORS[t.tierType] || "bg-muted text-foreground"}`}>
                {TIER_LABELS[t.tierType] || t.tierType} · {formatNaira(t.price)}
              </span>
            ))}
          </div>
        )}
        <p className="pt-1 flex items-center justify-between">
          <span className="text-[13px] text-muted-foreground flex items-center gap-1">
            <Users className="size-3.5" /> {event.totalRegistrations} registered
          </span>
          {lowestTier && (
            <span>
              <span className="text-[11px] text-muted-foreground">From </span>
              <span className="font-semibold text-foreground">{formatNaira(lowestTier.price)}</span>
              <span className="text-sm text-muted-foreground"> / ticket</span>
            </span>
          )}
        </p>
      </Link>
    </div>
  );
}

function EventsPage() {
  const { isAuthenticated } = useAuth();
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    eventsApi.list({ status: "active", pageSize: 100 })
      .then(res => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = events.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.venueName.toLowerCase().includes(search.toLowerCase()) ||
    e.organizer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>

      <section className="bg-gradient-to-b from-primary/8 to-background pt-28 pb-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-4xl font-bold">Events</h1>
              <p className="mt-2 text-muted-foreground text-base">Concerts, conferences, parties & more in Oyo State</p>
            </div>
            {isAuthenticated && (
              <Link to="/events/create">
                <Button className="gap-2 rounded-full"><Plus className="size-4" /> Create event</Button>
              </Link>
            )}
          </div>
          <div className="mt-6 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search events, venues, organizers…" className="pl-10 rounded-full" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Loading events…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            <Ticket className="size-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No events found</p>
            <p className="text-sm mt-1">{events.length === 0 ? "Check back soon for upcoming events." : "Try a different search term"}</p>
          </div>
        ) : (
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {filtered.map(event => <EventCard key={event.id} event={event} />)}
          </div>
        )}
        {!isAuthenticated && (
          <div className="mt-12 rounded-2xl border border-primary/20 bg-primary/5 p-6 text-center">
            <Calendar className="size-10 mx-auto mb-3 text-primary" />
            <h3 className="font-display text-lg font-semibold">Hosting an event?</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Sign in to create events with tiered ticketing and accept payments.</p>
            <Link to="/login" search={{ redirect: "/events/create" }}>
              <Button variant="default" className="rounded-full">Sign in to create</Button>
            </Link>
          </div>
        )}
      </section>
    </>
  );
}
